import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  TODAY_INTENSITY_CHOICES,
  TODAY_MINUTE_CHOICES,
  TODAY_REST_HOURS_CHOICES,
  type TodayRestHours,
} from '@heraion/shared';

import { Button } from '../primitives/Button';
import { ChipChoiceRow } from '../primitives/ChipChoiceRow';
import { ToggleRow } from '../primitives/ToggleRow';
import type { Theme } from './theme';

// The header box of the «today» draft (mockup 2026-09-26-today-draft-header):
// the time chips, the summary, the scope and the shuffle for every row. Its
// own fill, so the summary never reads as one more exercise row. P0 57d74690
// (D7, mockup approved 01/10): the intensity chips under the time chips, and
// «Surprise me» under those. Antenore 07/10: the rest chips sit with them.

/** The intensity and «Surprise me», from useTodayDraft. */
export type TodayDraftTuning = {
  /** Percent of the weekly target, one of TODAY_INTENSITY_CHOICES. */
  intensity: number;
  onIntensity: (pct: number) => void;
  surprise: boolean;
  onSurprise: (on: boolean) => void;
};

/** The time budget and the shuffles, from useTodayDraft. Absent: the card
 *  shows the draft as it is (a draft saved before AP-9c). */
export type TodayDraftControls = {
  minutes: number;
  onMinutes: (minutes: number) => void;
  /** Whole minutes the draft's sets take, rest included. */
  estimatedMin: number;
  onShuffleRow: (exerciseId: string) => void;
  onShuffleAll: () => void;
  /** Absent: a LEGACY draft (TodayAlternative.last). */
  tuning?: TodayDraftTuning;
  /** The rest the draft kept; null when it ignored rest («anyway»). Absent:
   *  a draft saved before the rest chips. */
  rest?: { hours: TodayRestHours | null; onHours: (hours: TodayRestHours) => void };
};

type Props = {
  /** Absent: a draft saved before AP-9c, no chips and no shuffle. */
  controls?: TodayDraftControls;
  exerciseCount: number;
  totalSets: number;
  /** Absent: a draft saved before 48/72 h, no scope line. */
  restHours?: number;
  /** «Build one anyway»: the scope line says rest was ignored. */
  ignoreRest?: boolean;
  /** A MAV row has sets: the scope line names MAV too. */
  hasMavRows: boolean;
  /** At least one row in the draft has another exercise to shuffle to. */
  canShuffleAll: boolean;
  theme: Theme;
};

export function TodayDraftHeader({
  controls,
  exerciseCount,
  totalSets,
  restHours,
  ignoreRest = false,
  hasMavRows,
  canShuffleAll,
  theme,
}: Props) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const tuning = controls?.tuning;
  const rest = controls?.rest;
  return (
    <View style={styles.box}>
      {controls ? (
        <View style={styles.controls}>
          <ChipChoiceRow
            theme={theme}
            choices={TODAY_MINUTE_CHOICES}
            selected={controls.minutes}
            onSelect={controls.onMinutes}
            label={String}
            chipA11yLabel={(n) => t('mobile.logger.draftToday.minutes', { n })}
            groupA11yLabel={t('mobile.logger.draftToday.timeA11y')}
            title={t('mobile.logger.draftToday.timeTitle')}
          />
          {tuning ? (
            <ChipChoiceRow
              theme={theme}
              choices={TODAY_INTENSITY_CHOICES}
              selected={tuning.intensity}
              onSelect={tuning.onIntensity}
              label={(n) => t('mobile.logger.draftToday.intensityChoice', { n })}
              chipA11yLabel={(n) => t('mobile.logger.draftToday.intensityChipA11y', { n })}
              groupA11yLabel={t('mobile.logger.draftToday.intensityA11y')}
              title={t('mobile.logger.draftToday.intensityTitle')}
            />
          ) : null}
          {rest ? (
            <ChipChoiceRow
              theme={theme}
              choices={TODAY_REST_HOURS_CHOICES}
              selected={rest.hours}
              onSelect={rest.onHours}
              label={(n) => t('mobile.logger.draftToday.restChoice', { n })}
              chipA11yLabel={(n) => t('mobile.logger.draftToday.restChipA11y', { count: n })}
              groupA11yLabel={t('mobile.logger.draftToday.restA11y')}
              title={t('mobile.logger.draftToday.restTitle')}
            />
          ) : null}
          {tuning ? (
            <ToggleRow
              theme={theme}
              label={t('mobile.logger.draftToday.surprise')}
              sublabel={t('mobile.logger.draftToday.surpriseHint')}
              value={tuning.surprise}
              onValueChange={tuning.onSurprise}
            />
          ) : null}
        </View>
      ) : null}
      <Text style={styles.big}>
        {t('mobile.logger.draftToday.exercises', { count: exerciseCount })}
        {' · '}
        {t('progress.volumeLandmark.sets', { count: totalSets, n: totalSets })}
        {controls && exerciseCount > 0
          ? ` · ${t('mobile.logger.draftToday.estimate', { n: controls.estimatedMin })}`
          : ''}
        {tuning && tuning.intensity < 100
          ? ` · ${t('mobile.logger.draftToday.intensityToday', { n: tuning.intensity })}`
          : ''}
      </Text>
      {ignoreRest ? (
        <Text style={styles.small}>{t('mobile.logger.draftToday.scopeAnyway')}</Text>
      ) : restHours !== undefined ? (
        <Text style={styles.small}>
          {t(hasMavRows ? 'mobile.logger.draftToday.scopeMav' : 'mobile.logger.draftToday.scope', {
            hours: restHours,
          })}
        </Text>
      ) : null}
      {controls && canShuffleAll ? (
        <Button
          theme={theme}
          variant="secondary"
          icon="repeat"
          label={t('mobile.logger.draftToday.shuffleAll')}
          accessibilityHint={t('mobile.logger.draftToday.shuffleAllA11y')}
          onPress={controls.onShuffleAll}
          style={styles.shuffle}
        />
      ) : null}
    </View>
  );
}

function makeStyles(theme: Theme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    // The veto box's fill (surface2, r3). borderStrong: `border` is ~1.1:1
    // against it, and the edge is what keeps the header apart from the rows.
    box: {
      backgroundColor: p.surface2,
      borderColor: p.borderStrong,
      borderWidth: 1,
      borderRadius: r.r3,
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s2,
    },
    // s4 (12) between blocks: a chip row's 8 of bottom slop ends before the
    // next block's title, and that title holds the next row's top slop.
    controls: { gap: s.s4, marginBottom: s.s2 },
    big: {
      color: p.ink,
      fontSize: ty.sizes.md,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
    },
    small: { color: p.ink2, fontSize: ty.sizes.sm, lineHeight: ty.sizes.sm * 1.4 },
    shuffle: { alignSelf: 'flex-start', marginTop: s.s2 },
  });
}
